import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ErrorToast, SuccessToast } from "../components/toastMessage";
import { _axios } from "../utils/axios";
import {
  TSavedDemandNoteData,
  level3ApplicationsKeyStore,
} from "./level-3-application-approval.hooks";

export type TLevel3PaymentReceipt = {
  receipt_id: number;
  receipt_no?: number;
  amount_paid: number;
  payment_date?: string;
  receipt_file?: string;
  application_id: string;
  payment_status:
  | "Draft"
  | "Payment Acknowledgment"
  | "Payment Discrepancy"
  | "Payment Successful";
};

export const level3PaymentKeyStore = {
  plotShapes: ["plot-shapes"],
  certificateData: (...args: (string | number | undefined)[]) => [
    "level-3-certificate-data",
    ...args,
  ],
  demandNoteData: (...args: (number | string | undefined)[]) => [
    "level-3-payment-demand-note-data",
    ...args,
  ],
  lastReview: (id?: number | string) => ["level-3-payment-last-review", id],
  receipts: (id?: number | string) => ["level-3-payment-receipts", id],
  document: (id?: number | string) => ["doclist", id],
};

export function useApplicationReceiptsForLevel3({
  id,
}: {
  id: number | string;
}) {
  return useQuery({
    queryKey: level3PaymentKeyStore.receipts(id),
    queryFn: () =>
      _axios.get<{
        success: true;
        data: TLevel3PaymentReceipt[];
      }>(`/level-3-payment-approval/receipts/${id}`),
    select: (d) => d?.data?.data,
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });
}

export function useLevel3UpdateReceiptStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      receiptId: number;
      status: "Payment Acknowledgment" | "Payment Discrepancy";
      applicationId: number;
    }) => _axios.patch("/level-3-payment-approval/receipts/update", payload),
    onSettled: (_, __, { applicationId }) =>
      qc.invalidateQueries({
        queryKey: level3PaymentKeyStore.receipts(applicationId),
        exact: true,
      }),
    onError: () => {
      ErrorToast("Something went wrong!");
    },
    onSuccess: () => {
      SuccessToast("Updated!");
    },
  });
}

export function useLevel3LastPaymentReview({ id }: { id?: number }) {
  return useQuery({
    queryKey: level3PaymentKeyStore.lastReview(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data?: { comment_text?: string; id?: number };
      }>(`/level-3-payment-approval/review/${id}`),
    select: (result) => result.data?.data,
  });
}

export function useLevel3Documents({ id }: { id?: number }) {
  return useQuery({
    queryKey: level3PaymentKeyStore.document(id),
    enabled: !!id,
    queryFn: () =>
      _axios.get<{
        success: true;
        data?: any;
      }>(`/level-three-documents/doclist/${id}`),
    select: (result) => result,
  });
}

export function useSaveLevel3PaymentReview() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; comment: string }) =>
      _axios.post("/level-3-payment-approval/review", data),
    onSuccess: () => SuccessToast("Saved!"),
    onError: () => ErrorToast("Something went wrong while saving review!"),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level3PaymentKeyStore.lastReview(applicationId),
      });
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.allReviews(applicationId),
      });
    },
  });
}

export function useLevel3PaymentRejectApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      application_id: string;
      user_id: number;
    }) => _axios.post("/level-3-payment-approval/reject-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application rejected successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while rejecting application!"),
  });
}

export function useLevel3PaymentReturnApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      applicationId: number;
      application_id: string;
      user_id: number;
    }) => _axios.post("/level-3-payment-approval/return-application", data),
    onSettled: (_, __, { applicationId }) => {
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.stats() });
    },
    onSuccess: () => SuccessToast("Application returned successfully!"),
    onError: () =>
      ErrorToast("Something went wrong while returning application!"),
  });
}

export function useDemandNoteData({
  applicationId,
}: {
  applicationId?: number;
}) {
  return useQuery({
    queryKey: level3PaymentKeyStore.demandNoteData(applicationId),
    select: (d) => d.data?.data,
    queryFn: () =>
      _axios.get<{
        success: true;
        data?: { id: number } & TSavedDemandNoteData;
      }>(`/level-3-payment-approval/demand-note-data/${applicationId}`),
  });
}

export type TCertificateData = {
  applicantName?: string;
  applicationNumber?: string;
  address?: string;
  layoutName?: string;
  mauja?: string;
  khasaraNo?: string;
  main_khasara_no?: string;
  manual_khasara_no?: string;
  layoutHolderName?: string;
  layoutHolderAddress?: string;
  ownershipDocumentNo?: string;
  ownershipDocumentDate?: string;
  plotNo?: string;
  plotShape?: string;
  schematicDimension?: string;
  receiptNumber?: string;
  constructionType?: string;
  permitNo?: string;
  municipality?: string;
  tehsil?: string;
  district?: string;
  permitDate?: string;
  applicationSubmissionDate?: string;
  demandNoteAmountPaid?: number;
  plotAreaAsPerSaleDeed?: number;
  regularizedArea?: number;
  deductionInPlotArea?: number;
  builtUpArea?: number;
  ab?: number;
  bc?: number;
  cd?: number;
  da?: number;
  areaToSurrenderToNagarParishad?: number;
  areaOfConstructionToBeDemolished?: number;
  direction?: string | number;
  conditions?: string;
  paymentReceiptDate?: string;
  ulcComment?: string;
  copyTo?: string;
};

export function useLevel3CertificateData({
  applicationId,
  reviewId,
}: {
  reviewId?: number | string;
  applicationId?: number | string;
}) {
  return useQuery({
    queryKey: level3PaymentKeyStore.certificateData(applicationId, reviewId),
    enabled: !!applicationId && !isNaN(Number(applicationId)) && !!reviewId && !isNaN(Number(reviewId)) && Number(reviewId) > 0,
    select: (d) => d?.data?.data,
    queryFn: () =>
      _axios.get<{ data?: TCertificateData | null; success: true }>(
        "/level-3-payment-approval/certificate-data",
        {
          params: { reviewId, applicationId },
        }
      ),
  });
}

export function useLevel3SaveCertificateData() {
  return useMutation({
    mutationFn: (payload: {
      applicationId: number;
      reviewId: number;
      data: Record<string, unknown>;
    }) =>
      _axios.post(
        "/level-3-payment-approval/certificate-data",
        payload,
        {
          responseType: "blob", // ✅ tells Axios + TS this is binary
        }
      ),
    onError: () =>
      ErrorToast("Something went wrong while generating certificate!"),
  });
}

export type TPlotShape = {
  plot_shape: string;
  id: number;
};

export function usePlotShapes() {
  return useQuery({
    queryKey: level3PaymentKeyStore.plotShapes,
    queryFn: () =>
      _axios.get<{ success: true; data: TPlotShape[] }>(
        "/level-3-payment-approval/plot-shapes"
      ),
    staleTime: 5 * 1000,
    select: (result) => result?.data?.data,
  });
}

export function useGenerateCertificateForLevel3() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { applicationId: number; reviewId: number }) =>
      _axios.post<{ success: true }>(
        "level-3-payment-approval/generate-certificate",
        data
      ),
    onSuccess: (_, { applicationId }) => {
      SuccessToast("Application Forwarded!");
      qc.invalidateQueries({
        queryKey: level3ApplicationsKeyStore.id(applicationId),
      });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.list() });
      qc.invalidateQueries({ queryKey: level3ApplicationsKeyStore.stats() });
    },
    onError: () =>
      ErrorToast("Something went wrong while forwarding application!"),
  });
}
